import React, { useState, useEffect, useCallback } from 'react';
import {
  getCrmOffers, previewCrmOffers, createCrmOffer, getCrmOffer, updateCrmOfferItem,
  getCrmOfferWalletOptions, getCrmOfferStatus, runCrmOffer, undoCrmOffer,
} from '../../services/api';
import { RFM_TITLES, RfmSelect } from './rfmSegments';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatMoney, formatDate } from '../../utils/formatters';

/* Иконки рисуем штрихом вместо эмодзи (см. Navbar.jsx). */
const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IconSearch = () => <Icon><circle cx="11.5" cy="11.5" r="9.5" /><path d="M18.5 18.5L22 22" /></Icon>;
const IconWarning = () => <Icon><path d="M5.31171 10.7615C8.23007 5.58716 9.68925 3 12 3C14.3107 3 15.7699 5.58716 18.6883 10.7615L19.0519 11.4063C21.4771 15.7061 22.6897 17.856 21.5937 19.428C20.4978 21 17.7864 21 12.3637 21H11.6363C6.21356 21 3.50217 21 2.40626 19.428C1.31034 17.856 2.52291 15.7061 4.94805 11.4063L5.31171 10.7615Z" /><path d="M12 8V13" /><path d="M12 16H12.0001" /></Icon>;

const STATUS_TITLES = { active: 'Активные', risk: 'Под риском', lost: 'Потерянные' };
const OFFER_STATUS_TITLES = { draft: 'Черновик', running: 'Выполняется', done: 'Завершены', reversed: 'Отменены', interrupted: 'Прерваны', reversing: 'Отменяются', reverse_partial: 'Отменены не полностью' };
// Откуда можно отменить начисление — совпадает с UNDOABLE_CAMPAIGN_STATUSES в server.py.
const UNDOABLE = new Set(['done', 'interrupted', 'reverse_partial']);
const WARN_STATUSES = new Set(['running', 'reversing', 'interrupted', 'reverse_partial']);
const ITEM_STATUS_TITLES = {
  suggested: 'Готово к отправке', skipped_no_data: 'Нет данных для расчёта',
  credited: 'Начислено', not_found: 'Не найден в Cloud', skipped_no_wallet: 'Нет такого кошелька',
  failed: 'Ошибка', reversed: 'Списано обратно',
};
const KIND_TITLES = {
  win_back_lost: 'Возврат потерянного гостя',
  win_back_risk: 'Профилактика ухода',
  nurture_valuable: 'Поддержка ценного гостя',
  loyalty_reward: 'Поощрение постоянного гостя',
  second_visit: 'Приглашение на второй визит',
  thank_you: 'Благодарность за визиты',
};
const WIN_BACK_KINDS = new Set(['win_back_lost', 'win_back_risk']);
const CHANNEL_TITLES = { telegram: 'Telegram', max: 'MAX' };
const MESSAGE_STATUS_TITLES = {
  sent: 'отправлено', not_linked: 'гость не привязан', failed: 'ошибка отправки',
};

const NewOfferForm = ({ onCreated, preset }) => {
  // preset — сегмент, с которым пришли из RFM-таблицы аналитики
  const [name, setName] = useState(preset?.rfm ? `${RFM_TITLES[preset.rfm] || preset.rfm} — ${new Date().toLocaleDateString('ru-RU', { month: 'long' })}` : '');
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState(preset?.status || '');
  const [tag, setTag] = useState('');
  const [search, setSearch] = useState('');
  const [rfm, setRfm] = useState(preset?.rfm || '');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const filters = { status, tag, search, rfm };

  useEffect(() => {
    let cancelled = false;
    previewCrmOffers(filters).then((res) => { if (!cancelled) setPreview(res); }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, tag, search, rfm]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await createCrmOffer({ name: name.trim(), comment: comment.trim() || null, filters });
      setName(''); setComment(''); setStatus(''); setTag(''); setSearch(''); setRfm('');
      onCreated();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="panel crm-promo-form" onSubmit={submit}>
      <h4>Новые персональные предложения</h4>
      <div className="crm-promo-form-row">
        <input type="text" placeholder="Название (например «Возврат гостей — сентябрь»)"
               value={name} onChange={(e) => setName(e.target.value)} />
        <input type="text" placeholder="Комментарий (необязательно)" value={comment}
               onChange={(e) => setComment(e.target.value)} />
      </div>
      <div className="crm-promo-form-row">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Любой статус</option>
          {Object.entries(STATUS_TITLES).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </select>
        <RfmSelect value={rfm} onChange={setRfm} />
        <input type="text" placeholder="Тег" value={tag} onChange={(e) => setTag(e.target.value)} />
        <input type="text" placeholder="Поиск по имени/телефону/карте — оставьте одного гостя для точечного предложения"
               value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {preview && (
        <div className="crm-promo-preview">
          Подходит под сегмент: <strong>{preview.matched}</strong> гостей.
          Персонализированный текст и сумма считаются каждому по его профилю; начислить бонус в iiko
          можно только тем, у кого известен телефон: <strong>{preview.with_phone}</strong>.
        </div>
      )}
      <button className="btn-primary" type="submit" disabled={busy || !name.trim()}>
        Сформировать предложения (черновик — ничего не начисляется)
      </button>
    </form>
  );
};

const OfferItemRow = ({ item, offerId, editable, onChanged }) => {
  const [amount, setAmount] = useState(item.amount);
  const [messageText, setMessageText] = useState(item.message_text || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setAmount(item.amount); }, [item.amount]);
  useEffect(() => { setMessageText(item.message_text || ''); }, [item.message_text]);

  const saveAmount = async () => {
    const value = Math.max(0, Number(amount) || 0);
    if (value === item.amount) return;
    setBusy(true);
    try { await updateCrmOfferItem(offerId, item.id, { amount: value }); onChanged(); } finally { setBusy(false); }
  };

  const saveMessageText = async () => {
    const value = messageText.trim();
    if (value === (item.message_text || '')) return;
    setBusy(true);
    try { await updateCrmOfferItem(offerId, item.id, { message_text: value }); onChanged(); } finally { setBusy(false); }
  };

  const toggleExcluded = async () => {
    setBusy(true);
    try {
      await updateCrmOfferItem(offerId, item.id, { excluded: !item.excluded });
      onChanged();
    } finally { setBusy(false); }
  };

  const canEdit = editable && item.status === 'suggested';

  return (
    <tr className={item.excluded ? 'crm-offer-item-excluded' : ''}>
      <td onClick={(e) => e.stopPropagation()}>
        {editable && (
          <input type="checkbox" checked={!item.excluded} onChange={toggleExcluded} disabled={busy}
                 title="Включить/исключить из рассылки" />
        )}
      </td>
      <td>
        <div className="gb-name">{item.name || item.card_number || '—'}</div>
        {item.phone && <div className="gb-owner">{item.phone}</div>}
        {!item.phone && <div className="gb-owner">нет телефона — только текст, без начисления</div>}
        {item.linked_channel && (
          <span className="gb-badge st-active crm-offer-channel-badge">
            {CHANNEL_TITLES[item.linked_channel] || item.linked_channel}
          </span>
        )}
      </td>
      <td>
        {item.status_at_creation && (
          <span className={`gb-badge st-${item.status_at_creation}`}>
            {STATUS_TITLES[item.status_at_creation] || 'Нет данных'}
          </span>
        )}
      </td>
      <td className="crm-offer-reason">
        {item.kind && <div className="crm-offer-kind">{KIND_TITLES[item.kind] || item.kind}</div>}
        <div>{item.reason_text}</div>
      </td>
      <td className="crm-offer-message-text" onClick={(e) => e.stopPropagation()}>
        {canEdit ? (
          <textarea rows={2} className="crm-offer-message-input" value={messageText}
                    placeholder={item.default_message || 'Для этого предложения нет автотекста — можно задать своё'}
                    onChange={(e) => setMessageText(e.target.value)} onBlur={saveMessageText} disabled={busy} />
        ) : (
          <div className="crm-offer-message-preview">{item.message_text || item.default_message || '—'}</div>
        )}
        {canEdit && item.message_text && (
          <button type="button" className="crm-offer-message-reset"
                  onClick={() => { setMessageText(''); updateCrmOfferItem(offerId, item.id, { message_text: '' }).then(onChanged); }}
                  disabled={busy}>
            Сбросить к автотексту
          </button>
        )}
      </td>
      <td className="gb-money" onClick={(e) => e.stopPropagation()}>
        {canEdit ? (
          <input type="number" min="0" className="crm-offer-amount-input" value={amount}
                 onChange={(e) => setAmount(e.target.value)} onBlur={saveAmount} disabled={busy} />
        ) : (
          formatMoney(item.amount)
        )}
      </td>
      <td>
        <div>{ITEM_STATUS_TITLES[item.status] || item.status}{item.detail ? ` — ${item.detail}` : ''}</div>
        {item.message_status && (
          <div className="crm-offer-message-status">
            {CHANNEL_TITLES[item.message_channel] || 'Мессенджер'}: {MESSAGE_STATUS_TITLES[item.message_status] || item.message_status}
            {item.message_detail ? ` — ${item.message_detail}` : ''}
          </div>
        )}
        {WIN_BACK_KINDS.has(item.kind) && item.status === 'credited' && (
          item.converted_at
            ? <div className="crm-offer-conversion crm-offer-conversion-yes">Вернулся(ась) {formatDate(item.converted_at)}</div>
            : <div className="crm-offer-conversion">Пока не вернулся(ась)</div>
        )}
      </td>
    </tr>
  );
};

const OfferItems = ({ offerId, offerStatus, onOfferChanged }) => {
  const [data, setData] = useState(null);
  const [walletOptions, setWalletOptions] = useState([]);
  const [walletName, setWalletName] = useState('');
  const [walletBusy, setWalletBusy] = useState(false);
  const [walletError, setWalletError] = useState('');
  const [runBusy, setRunBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await getCrmOffer(offerId);
    setData(res);
  }, [offerId]);

  // offerStatus меняется снаружи (draft -> running -> done — см. поллинг в
  // OffersTab), а сами item'ы этого предложения лежат в отдельном состоянии
  // здесь: без переподгрузки по смене статуса строки застревали бы на
  // "Готово к отправке" даже после реального запуска рассылки.
  useEffect(() => { load(); }, [load, offerStatus]);

  if (!data) return <LoadingSpinner />;

  const items = data.items || [];
  const active = items.filter((it) => !it.excluded && it.status !== 'skipped_no_data');
  const total = active.reduce((sum, it) => sum + (it.amount || 0), 0);
  const withPhone = active.filter((it) => it.phone).length;
  const withChannel = active.filter((it) => it.linked_channel).length;

  const loadWallets = async () => {
    setWalletBusy(true);
    setWalletError('');
    try {
      const res = await getCrmOfferWalletOptions(offerId);
      setWalletOptions(res.wallets || []);
      if (res.error) setWalletError(res.error);
      else if (res.wallets?.length) setWalletName(res.wallets[0]);
    } catch (err) {
      setWalletError(err.response?.data?.error || 'Не удалось получить список кошельков');
    } finally {
      setWalletBusy(false);
    }
  };

  const run = async () => {
    if (!walletName) return;
    const ok = window.confirm(
      `Будет начислена персональная сумма бонусами каждому из ${withPhone} гостей ` +
      `(кошелёк «${walletName}») реальными деньгами в iiko, всего ~${total} ₽. ` +
      `Действие сложно отменить одним кликом. Продолжить?`
    );
    if (!ok) return;
    setRunBusy(true);
    try {
      await runCrmOffer(offerId, walletName);
      onOfferChanged();
    } finally {
      setRunBusy(false);
    }
  };

  const undo = async () => {
    const ok = window.confirm('Списать обратно все начисленные по этим предложениям бонусы? ' +
      'Если гость уже успел их потратить, списание уйдёт в минус его баланса в iiko.');
    if (!ok) return;
    await undoCrmOffer(offerId);
    onOfferChanged();
  };

  return (
    <div className="table-wrapper crm-promo-results">
      <table>
        <thead>
          <tr><th></th><th>Гость</th><th>Статус</th><th>Предложение</th><th>Текст гостю</th><th>Сумма</th><th>Итог</th></tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <OfferItemRow key={item.id} item={item} offerId={offerId}
                          editable={offerStatus === 'draft'} onChanged={load} />
          ))}
          {items.length === 0 && <tr><td colSpan={7} className="crm-empty">Пока пусто</td></tr>}
        </tbody>
      </table>

      {offerStatus === 'draft' && (
        <div className="crm-offer-run-bar">
          <div>Активных предложений: <strong>{active.length}</strong>, с телефоном: <strong>{withPhone}</strong>,
            привязаны к Telegram: <strong>{withChannel}</strong>,
            общая сумма: <strong>{formatMoney(total)}</strong></div>
          {withChannel > 0 && (
            <div className="crm-promo-preview">
              При запуске гостям, привязанным к Telegram, отдельно уйдёт персональный текст —
              бонус и сообщение независимы: одно может пройти без другого.
            </div>
          )}
          <div className="crm-promo-form-row" style={{ marginBottom: 0 }}>
            <select value={walletName} onChange={(e) => setWalletName(e.target.value)} disabled={!walletOptions.length}>
              <option value="">{walletOptions.length ? 'Кошелёк' : 'Сначала проверьте кошельки'}</option>
              {walletOptions.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
            <button type="button" className="ui-btn-ghost" onClick={loadWallets} disabled={walletBusy}>
              {walletBusy ? 'Проверяю…' : <><IconSearch /> Проверить кошельки</>}
            </button>
            <button type="button" className="btn-primary" onClick={run} disabled={runBusy || !walletName || withPhone === 0}>
              Запустить рассылку
            </button>
          </div>
          {walletError && <div className="crm-promo-error"><IconWarning /> {walletError}</div>}
        </div>
      )}

      {UNDOABLE.has(offerStatus) && (
        <div className="crm-offer-run-bar">
          <button className="ui-btn-ghost" onClick={undo}>Отменить начисление</button>
        </div>
      )}
    </div>
  );
};

const OfferRow = ({ offer, onStarted, progress }) => {
  const [expanded, setExpanded] = useState(false);

  const isRunningThis = progress?.running && progress.offer_id === offer.id;
  const badgeClass = offer.status === 'done' ? 'st-active'
    : offer.status === 'reversed' ? 'st-lost'
    : WARN_STATUSES.has(offer.status) ? 'st-risk' : 'st-unknown';

  return (
    <>
      <tr className="crm-row" onClick={() => setExpanded((v) => !v)}>
        <td>
          <div className="gb-name">{offer.name}</div>
          {offer.comment && <div className="gb-owner">{offer.comment}</div>}
        </td>
        <td>{offer.wallet_name || '—'}</td>
        <td><span className={`gb-badge ${badgeClass}`}>{OFFER_STATUS_TITLES[offer.status] || offer.status}</span></td>
        <td>
          {isRunningThis
            ? `${progress.checked} / ${progress.total}`
            : (offer.status === 'draft'
              ? '—'
              : `начислено ${offer.credited_count} · пропущено ${offer.skipped_count} · ошибок ${offer.failed_count}`)}
        </td>
        <td>
          {offer.win_back_total > 0
            ? `${offer.win_back_returned} из ${offer.win_back_total} (${Math.round(100 * offer.win_back_returned / offer.win_back_total)}%)`
            : '—'}
        </td>
        <td>{offer.created_at ? formatDate(offer.created_at) : '—'}</td>
      </tr>
      {expanded && (
        <tr><td colSpan={6}>
          <OfferItems offerId={offer.id} offerStatus={offer.status} onOfferChanged={onStarted} />
        </td></tr>
      )}
    </>
  );
};

const OffersTab = ({ preset }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [pollTrigger, setPollTrigger] = useState(0);

  const load = useCallback(async () => {
    const res = await getCrmOffers();
    setOffers(res.offers || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const res = await getCrmOfferStatus();
        if (cancelled) return;
        setProgress(res);
        if (res.running) {
          timer = setTimeout(poll, 2000);
        } else {
          load();
        }
      } catch {
        // тихо — просто перестаём опрашивать
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [load, pollTrigger]);

  return (
    <>
      <NewOfferForm onCreated={load} preset={preset} />

      {loading && <LoadingSpinner />}

      {!loading && (
        <div className="panel gb-table-panel">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Предложение</th><th>Кошелёк</th><th>Статус</th><th>Итог</th><th>Возврат гостей</th><th>Создано</th></tr>
              </thead>
              <tbody>
                {offers.map((o) => (
                  <OfferRow
                    key={o.id} offer={o}
                    onStarted={() => setPollTrigger((n) => n + 1)}
                    progress={progress}
                  />
                ))}
                {offers.length === 0 && (
                  <tr><td colSpan={6} className="crm-empty">Пока нет предложений</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default OffersTab;
